import { formatTime } from './format';
import { frameRect, type BoardRect, type Viewport } from '../play/layout';
import { fillStars } from './stars';
import { runeSvg } from './runeIcons';

export type HudState = { timeMs: number; progress: number };
export type HudActions = { preview: () => void; hint: () => void; restart: () => void };
/** rune: null = 这张图没有符文（城市），不显示 */
export type ResultState = { timeMs: number; moves: number; catFound: boolean; rune?: boolean | null; newBestTime?: boolean; newBestMoves?: boolean; stars?: number | null };

const $ = <T extends HTMLElement = HTMLElement>(sel: string): T => {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`Missing ${sel}`);
  return el;
};
const setText = (el: HTMLElement, v: string) => {
  if (el.textContent !== v) el.textContent = v;
};

export class Hud {
  private readonly time = $('#hud-time');
  private readonly progress = $('#hud-progress');
  private readonly frame = $('#board-frame');
  private readonly result = $('#result');
  private readonly resTime = $('#res-time');
  private readonly resMoves = $('#res-moves');
  private readonly resCat = $('#res-cat');
  private readonly resStars = $('#res-stars');
  private readonly resRune = $('#res-rune');

  bind(a: HudActions): void {
    $('#btn-preview').onclick = a.preview;
    $('#btn-hint').onclick = a.hint;
    $('#btn-restart').onclick = a.restart;
  }

  set(s: HudState): void {
    setText(this.time, formatTime(s.timeMs));
    setText(this.progress, `${Math.round(s.progress * 100)}%`);
  }

  /** 纸板框叠层贴着棋盘：位置完全由 boardRect 推出（见 layout.frameRect），不手调 */
  placeFrame(vp: Viewport, r: BoardRect): void {
    const f = frameRect(vp, r);
    const st = this.frame.style;
    st.left = `${f.left}px`;
    st.top = `${f.top}px`;
    st.width = `${f.width}px`;
    st.height = `${f.height}px`;
  }

  /** 立体书页：棋盘框 / 胶囊 / 底部按钮淡出（CSS 过渡），只留顶栏 */
  setPopup(on: boolean): void {
    $('#app').classList.toggle('is-popup', on);
    if (!on) this.hideResult();
  }

  bindHome(onHome: () => void): void {
    $('#btn-home').onclick = onHome;
  }

  bindBack(onBack: () => void): void {
    $('#btn-back').onclick = onBack;
  }

  setTitle(text: string): void {
    setText($('.ui-plaque'), text);
  }

  showResult(s: ResultState): void {
    this.result.classList.remove('is-daily');
    setText($('#btn-home span'), '回到地图');
    setText($('#res-record'), '新纪录');
    setText(this.resTime, formatTime(s.timeMs));
    setText(this.resMoves, String(s.moves));
    this.setCat(s.catFound);
    this.setRune(s.rune ?? null);
    this.result.classList.remove('is-view');
    $('#res-time').parentElement!.classList.toggle('is-record', !!s.newBestTime);
    $('#res-moves').parentElement!.classList.toggle('is-record', !!s.newBestMoves);
    $('#res-record').hidden = !(s.newBestTime || s.newBestMoves);
    this.setStars(s.stars ?? null);
    this.result.hidden = false;
  }

  /** 结算条上的星级（逐颗弹入，CSS 动画）；null = 不显示 */
  private setStars(got: number | null): void {
    this.resStars.hidden = got === null;
    if (got !== null) fillStars(this.resStars, got);
  }

  /** 每日挑战：没有立体书，棋盘上直接出结算条（用时、步数、打卡）；不显示猫 */
  showDailyResult(s: { timeMs: number; moves: number; first: boolean; streak: number; stars?: number | null }): void {
    this.setStars(s.stars ?? null);
    setText(this.resTime, formatTime(s.timeMs));
    setText(this.resMoves, String(s.moves));
    this.result.classList.remove('is-view');
    this.setRune(null);
    this.result.classList.add('is-daily');
    $('#res-time').parentElement!.classList.remove('is-record');
    $('#res-moves').parentElement!.classList.remove('is-record');
    const rec = $('#res-record');
    setText(rec, s.first ? `打卡 · 连续 ${s.streak} 天` : '今天已打卡，重玩不计');
    rec.hidden = false;
    setText($('#btn-home span'), '回到每日挑战');
    this.result.hidden = false;
  }

  /** 回看立体书的故事（null = 收起） */
  setStory(text: string | null): void {
    const el = $('#view-story');
    setText(el, text ?? '');
    el.hidden = text === null;
  }

  /** 查看立体书：结算条只留猫和返回按钮 */
  showView(catFound: boolean, homeLabel = '回到地图', rune: boolean | null = null): void {
    this.setCat(catFound);
    this.setRune(rune);
    this.result.classList.remove('is-daily');
    setText($('#btn-home span'), homeLabel);
    this.result.classList.add('is-view');
    this.setStars(null);
    $('#res-record').hidden = true;
    this.result.hidden = false;
  }

  setCat(found: boolean): void {
    setText(this.resCat.querySelector('b')!, found ? '●' : '○');
    this.resCat.classList.toggle('is-found', found);
    this.resCat.setAttribute('aria-label', found ? '猫：找到了' : '猫：没找到');
  }

  /** 符文：null = 不显示（城市 / 每日挑战） */
  setRune(found: boolean | null): void {
    this.resRune.hidden = found === null;
    this.result.classList.toggle('has-rune', found !== null);
    if (found === null) return;
    setText(this.resRune.querySelector('b')!, found ? '●' : '○');
    this.resRune.classList.toggle('is-found', found);
    this.resRune.setAttribute('aria-label', found ? '符文：找到了' : '符文：没找到');
  }

  /**
   * 找到符文：金色图标在点到的位置亮一下，然后飞进结算条的「符文」格，落下时那一格点亮。
   * (x, y) = 画布局部 CSS px（画布铺满 #app，与视口坐标一致）。
   */
  flyRune(icon: string, x: number, y: number): void {
    const fly = document.createElement('div');
    fly.className = 'rune-fly';
    fly.append(runeSvg(icon, 'rune-fly-icon'));
    $('#app').append(fly);
    const to = this.resRune.getBoundingClientRect();
    const tx = to.left + to.width - 14;
    const ty = to.top + to.height / 2;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const at = (px: number, py: number, s: number) => `translate(${px - 32}px, ${py - 32}px) scale(${s})`;
    const anim = fly.animate(
      [
        { transform: at(x, y, 0.4), opacity: 0, offset: 0 },
        { transform: at(x, y, 1.25), opacity: 1, offset: 0.22 },
        { transform: at(x, y, 1), opacity: 1, offset: 0.42 },
        { transform: at(tx, ty, 0.36), opacity: 0.9, offset: 1 },
      ],
      { duration: reduce ? 200 : 1150, easing: 'cubic-bezier(0.5, 0, 0.3, 1)', fill: 'forwards' },
    );
    anim.onfinish = () => {
      fly.remove();
      this.setRune(true);
      this.resRune.classList.remove('is-pop');
      void this.resRune.offsetWidth;
      this.resRune.classList.add('is-pop');
    };
  }

  hideResult(): void {
    this.result.hidden = true;
  }
}
